/**
 * Validation schemas using Zod
 * Used with React Hook Form for form validation
 */

import { z } from 'zod';

/**
 * Coordinate point schema for polygon vertices
 */
export const coordinateSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

/**
 * Polygon coordinates schema (array of coordinate points)
 */
export const polygonCoordinatesSchema = z.array(coordinateSchema).min(3, 'Polygon must have at least 3 points');

/**
 * Selection type schema
 */
export const selectionTypeSchema = z.enum(['rectangle', 'polygon']);

/**
 * Bounds validation schema
 */
export const boundsSchema = z.object({
  north: z.number().min(-90).max(90),
  south: z.number().min(-90).max(90),
  east: z.number().min(-180).max(180),
  west: z.number().min(-180).max(180),
}).refine((data) => data.north > data.south, {
  message: 'North must be greater than South',
  path: ['north'],
}).refine((data) => {
  // Handle antimeridian crossing
  if (data.west > data.east) {
    return true; // Valid antimeridian crossing
  }
  return data.east > data.west;
}, {
  message: 'East must be greater than West',
  path: ['east'],
});

/**
 * Job creation form schema
 */
export const createJobSchema = z.object({
  searchQuery: z
    .string()
    .min(2, 'Search query must be at least 2 characters')
    .max(200, 'Search query must be less than 200 characters')
    .trim(),

  locationName: z
    .string()
    .max(100, 'Location name must be less than 100 characters')
    .optional(),

  bounds: boundsSchema,

  polygon: polygonCoordinatesSchema.optional(),

  selectionType: selectionTypeSchema.default('rectangle'),

  zoomLevel: z.number().min(14).max(18).default(16),

  maxResultsPerCell: z.number().min(20).max(120).default(120),

  gridOverlapPercent: z.number().min(0).max(30).default(10),

  includeDetails: z.boolean().default(true),

  options: z.object({
    extractDetails: z.boolean().default(true),
    extractReviews: z.boolean().default(false),
    maxReviewsPerPlace: z.number().min(0).max(100).default(10),
    extractPhotos: z.boolean().default(false),
    zoomLevel: z.number().min(14).max(18).default(16),
    gridOverlapPercent: z.number().min(0).max(50).default(10),
    maxResults: z.number().min(0).optional(),
  }).default({}),

  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),

  scrambleOrder: z.boolean().default(true),
});

export type CreateJobFormData = z.infer<typeof createJobSchema>;

/**
 * Search query step schema
 */
export const queryStepSchema = z.object({
  searchQuery: z
    .string()
    .min(2, 'Search query must be at least 2 characters')
    .max(200, 'Search query must be less than 200 characters')
    .trim(),
});

export type QueryStepFormData = z.infer<typeof queryStepSchema>;

/**
 * Location step schema
 */
export const locationStepSchema = z.object({
  locationName: z.string().optional(),
  bounds: boundsSchema,
});

export type LocationStepFormData = z.infer<typeof locationStepSchema>;

/**
 * Options step schema
 */
export const optionsStepSchema = z.object({
  extractDetails: z.boolean(),
  extractReviews: z.boolean(),
  maxReviewsPerPlace: z.number().min(0).max(100),
  extractPhotos: z.boolean(),
  zoomLevel: z.number().min(14).max(18),
  gridOverlapPercent: z.number().min(0).max(50),
  maxResults: z.number().min(0).optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
});

export type OptionsStepFormData = z.infer<typeof optionsStepSchema>;

/**
 * Add proxy schema
 */
export const addProxySchema = z.object({
  host: z
    .string()
    .min(1, 'Host is required')
    .regex(
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
      'Invalid host format'
    ),
  port: z
    .number({ invalid_type_error: 'Port must be a number' })
    .int('Port must be an integer')
    .min(1, 'Port must be between 1 and 65535')
    .max(65535, 'Port must be between 1 and 65535'),
  protocol: z.enum(['http', 'https', 'socks5']),
  username: z.string().optional(),
  password: z.string().optional(),
});

export type AddProxyFormData = z.infer<typeof addProxySchema>;

/**
 * Settings schema
 */
export const settingsSchema = z.object({
  concurrency: z.number().min(1).max(10),
  delayMin: z.number().min(0).max(60000),
  delayMax: z.number().min(0).max(60000),
  timeout: z.number().min(5000).max(120000),
  retryAttempts: z.number().min(0).max(10),
}).refine((data) => data.delayMax >= data.delayMin, {
  message: 'Max delay must be greater than or equal to min delay',
  path: ['delayMax'],
});

export type SettingsFormData = z.infer<typeof settingsSchema>;

/**
 * Place filter schema
 */
export const placeFilterSchema = z.object({
  search: z.string().optional(),
  minRating: z.number().min(0).max(5).optional(),
  maxRating: z.number().min(0).max(5).optional(),
  hasPhone: z.boolean().optional(),
  hasWebsite: z.boolean().optional(),
  hasAddress: z.boolean().optional(),
  category: z.string().optional(),
  priceLevel: z.string().optional(),
}).refine((data) => {
  if (data.minRating !== undefined && data.maxRating !== undefined) {
    return data.maxRating >= data.minRating;
  }
  return true;
}, {
  message: 'Max rating must be greater than or equal to min rating',
  path: ['maxRating'],
});

export type PlaceFilterFormData = z.infer<typeof placeFilterSchema>;

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate URL format
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate coordinates
 */
export function isValidLatitude(lat: number): boolean {
  return lat >= -90 && lat <= 90;
}

export function isValidLongitude(lng: number): boolean {
  return lng >= -180 && lng <= 180;
}

/**
 * Validate bounds are reasonable (not too large)
 */
export function isReasonableBounds(bounds: {
  north: number;
  south: number;
  east: number;
  west: number;
}): { valid: boolean; message?: string } {
  const latDiff = bounds.north - bounds.south;
  const lngDiff = bounds.west > bounds.east
    ? (180 - bounds.west) + (bounds.east + 180) // Antimeridian crossing
    : bounds.east - bounds.west;

  // Maximum reasonable area is about 10,000 km²
  // Rough approximation: 1 degree ≈ 111 km at equator
  const areaApprox = latDiff * lngDiff * 111 * 111;

  if (areaApprox > 10000) {
    return {
      valid: false,
      message: 'Selected area is too large. Please select a smaller area.',
    };
  }

  if (areaApprox < 0.01) {
    return {
      valid: false,
      message: 'Selected area is too small. Please select a larger area.',
    };
  }

  return { valid: true };
}
